/* eslint-disable no-console */

import React, { useState, useEffect } from 'react';
import PageStyle from './style';
import { Input, Select, TableProps, Tooltip, message } from 'antd';
import { Button, Image, Pagination, Table } from 'src/components';
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { SpinStyle } from 'src/components/style';

import DeleteModal from './delete-modal/DeleteModal';
import FormModal from './form-modal/FormModal';
import SettingSortOrdersModal from './sort-orders-modal/SortOrdersModal';

import { useAppDispatch, useAppSelector } from 'src/stores';
import {
  deletePageSessionAction,
  listPageSessionAction,
} from 'src/stores/screens/pageSession/pageSession.action';
import { LANGUAGE, PAGE_LIMIT } from 'src/constants';
import { capitalizeFirstLetter } from 'src/utils';
import { FLAG_EN, FLAG_JA, FLAG_KO, FLAG_VI } from 'src/assets/images';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { detailPageAction } from 'src/stores/screens/page/page.action';
import Breadcrumb from 'src/components/breadcrumb';
import { COMPONENT_LABELS } from 'src/constants/form';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageSessionScreen: React.FC = () => {
  const navigator = useNavigate();
  const { pageId } = useParams();
  const dispatch = useAppDispatch();
  const { page } = useAppSelector((state) => state.page);
  const { sessions, isLoading } = useAppSelector((state) => state.pageSession);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [isModalSettingOrderOpen, setIsModalSettingOrderOpen] = useState(false);
  const [langue, setLangue] = useState(LANGUAGE.EN);
  const [currentRecord, setCurrentRecord] =
    useState<PageSession.PageSessionType | null>(null);
  const [filter, setFilter] = useState({
    keyword: '',
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCurrentPage();
    fetchData();
  }, [pageId]);

  useEffect(() => {}, []);

  const fetchData = (page = 1) => {
    if (!pageId) return;
    const params: PageSession.GetListPageSessionParams = cleanFilter({
      ...filter,
      page,
      sorts: 'sort_order-asc,created_at-asc'
    });
    void dispatch(
      listPageSessionAction({ data: { ...params, limit: PAGE_LIMIT, pageId } })
    );
    setCurrentPage(page);
  };

  const fetchCurrentPage = async () => {
    if (!pageId) return;
    await dispatch(detailPageAction({ data: pageId }));
  };

  const onFilterLangue = (value: string) => {
    setLangue(value);
  };

  const langues = [
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_EN} /> EN{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.EN,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_VI} /> VI{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.VI,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_JA} /> JA{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.JA,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_KO} /> KO{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.KO,
    },
  ];

  const columns: TableProps<any>['columns'] = [
    {
      title: '#',
      dataIndex: '#',
      key: '#',
      width: '80px',
      align: 'center',
      render: (id: string, _, index: number) => {
        const serialNumber = (currentPage - 1) * PAGE_LIMIT + (index + 1);
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '60px',
              whiteSpace: 'nowrap',
            }}
          >
            {serialNumber}
          </span>
        );
      },
    },
    {
      title: (
        <div className="langue-select">
          <div>Title</div>
          <div>
            <Select
              style={{ width: 120 }}
              placeholder="langue"
              maxTagCount="responsive"
              options={langues}
              value={langue}
              onChange={(value) => onFilterLangue(value)}
            />
          </div>
        </div>
      ),
      dataIndex: `title${capitalizeFirstLetter(langue)}`,
      key: `title${capitalizeFirstLetter(langue)}`,
      className: 'min-w-200',
      render: (value: string) => {
        return (
          <p>
            {value}
          </p>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: `description${capitalizeFirstLetter(langue)}`,
      key: `description${capitalizeFirstLetter(langue)}`,
      render: (value) => <p>{value}</p>
    },
    {
      title: 'Component',
      dataIndex: 'component',
      key: 'component',
      render: (value) => COMPONENT_LABELS[value]
    },
    {
      title: 'Media',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      width: '90px',
      align: 'center',
      render: (fileUrl, { fileType }) => {
        const isVideo = fileType?.includes('video')
        if (isVideo) return 'VIDEO'
        return fileUrl && !isVideo && <img src={fileUrl} style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
      }
    },
    {
      title: 'Show',
      dataIndex: 'isShow',
      key: 'isShow',
      width: '90px',
      align: 'center',
      render: (isShow) => isShow ? <CheckOutlined /> : ''
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      align: 'center',
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (_, record) => {
        return (
          <div className="action-box">
            <Tooltip placement="top" title={'Setting content'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator(`/page/${pageId}/${record.id}`);
                }}
              >
                <SettingOutlined />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={'Edit session'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditUser(record);
                }}
              >
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={'Delete session'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentRecord(record);
                  setIsModalDeleteOpen(true);
                }}
              >
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const cleanFilter = <T extends Record<string, any>>(obj: T): T => {
    const cleanedObj: Partial<T> = {};

    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== '') {
        cleanedObj[key as keyof T] = obj[key];
      }
    });

    return cleanedObj as T;
  };

  const onCreate = () => {
    setCurrentRecord(null);
    setIsModalFormOpen(true);
  };

  const onEditUser = (value: PageSession.PageSessionType) => {
    setUpUser(value);
    setIsModalFormOpen(true);
  };

  const handleDeleteOk = async () => {
    if (currentRecord?.id) {
      const result: any = await dispatch(
        deletePageSessionAction({ data: currentRecord.id })
      );
      setIsModalDeleteOpen(false);
      if (result?.error) {
        message.error('Delete failed');
      } else {
        message.success('Delete success');
        fetchData();
      }
    }
  };

  const setUpUser = (value: PageSession.PageSessionType) => {
    setCurrentRecord(value);
  };

  const handleOkFormUser = () => {
    handleCancelModal();
  };

  const onChangePage = (value: number) => {
    fetchData(value);
  };

  const handleCancelModal = () => {
    setIsModalFormOpen(false);
  };

  return !page ? (
    <></>
  ) : (
    <PageStyle className="container">
      <SpinStyle
        spinning={isLoading}
        wrapperClassName="root-spin"
        className="root-spin-component"
      >
        <div className="header-content">
          <Breadcrumb
            breadcrumbItems={[
              {
                name: 'Page',
                path: '/page',
              },
              {
                name: page?.pageNameEn,
                path: `/page/${page?.id}`,
              },
            ]}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button className="create-user" onClick={() => setIsModalSettingOrderOpen(true)}>
              Setting Order
            </Button>
            <Button className="create-user" onClick={() => onCreate()}>
              Create
            </Button>
          </div>
        </div>
        <div className="content">
          <div className="filter-box">
            <div className="total-box">Total: {sessions?.total}</div>
            <div className="search-box">
              <Input
                placeholder="Search title..."
                prefix={<SearchOutlined />}
                onChange={(e) =>
                  setFilter({ ...filter, keyword: e.target.value.trim() })
                }
                onPressEnter={() => fetchData()}
              />
              <Button onClick={() => fetchData()}>Search</Button>
            </div>
          </div>
          <div className="data-table">
            <Table
              dataSource={sessions.records || []}
              columns={columns}
              scroll={{ x: 'auto', y: sessions.records.length ? window.innerHeight - 380 : undefined }}
            />
            <Pagination
              total={sessions.total}
              onChange={onChangePage}
              current={currentPage}
              pageSize={sessions.limit}
            />
          </div>
        </div>
        <DeleteModal
          isModalDeleteOpen={isModalDeleteOpen}
          handleCancel={() => setIsModalDeleteOpen(false)}
          handleOk={handleDeleteOk}
        />
        {isModalFormOpen && (
          <FormModal
            isModalFormOpen={isModalFormOpen}
            handleCancel={handleCancelModal}
            handleOk={handleOkFormUser}
            pageId={pageId}
            currentRecord={currentRecord}
            fetchData={fetchData}
            currentPage={currentPage}
          />
        )}

        {isModalSettingOrderOpen && (
          <SettingSortOrdersModal
            isModalFormOpen={isModalSettingOrderOpen}
            handleCancel={() => setIsModalSettingOrderOpen(false)}
            handleOk={() => setIsModalSettingOrderOpen(false)}
            pageId={pageId}
            fetchData={fetchData}
          />
        )}
      </SpinStyle>
    </PageStyle>
  );
};

export default PageSessionScreen;
